// Feature: features/super-admin-dashboard.md
import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Box,
  Flex,
  Grid,
  GridItem,
  SimpleGrid,
  Heading,
  Text,
  Card,
  CardHeader,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  ButtonGroup,
  Select,
  Spinner,
  Alert,
  AlertIcon,
  Collapse,
  HStack,
  VStack,
  Code
} from '@chakra-ui/react';
import { RepeatIcon, ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { callApi } from '../callApi';
import { AppContext } from './AppContext';

const STALE_SYNC_MINUTES_WARNING = 60;
const STALE_SYNC_MINUTES_DANGER = 240;

const parseUtcDate = (isoString) => {
  if (!isoString) return null;
  const hasOffset = /([Zz]|[+-]\d{2}:?\d{2})$/.test(isoString);
  const dt = new Date(hasOffset ? isoString : `${isoString}Z`);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

const formatRelativeTime = (isoString) => {
  const target = parseUtcDate(isoString);
  if (!target) return 'Never';
  const diffMinutes = Math.max(0, Math.floor((Date.now() - target.getTime()) / 60000));
  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${Math.floor(diffHours / 24)}d ago`;
};

const formatTimestamp = (isoString) => {
  const target = parseUtcDate(isoString);
  if (!target) return '—';
  return target.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

const getStatusColor = (statusValue) => {
  const val = (statusValue || '').toUpperCase();
  if (['COMPLETED', 'SUCCESS', 'STARTED', 'INFO'].includes(val)) return 'green';
  if (val.includes('FAIL') || val.includes('ERROR') || val === 'CRITICAL') return 'red';
  if (val.includes('SKIP') || val.includes('WARN') || val === 'MANUAL_PREP') return 'orange';
  if (val.includes('PROCESS')) return 'blue';
  return 'gray';
};

const getSyncFreshnessScheme = (isoString) => {
  if (!isoString) return 'gray';
  const target = new Date(isoString.endsWith('Z') ? isoString : `${isoString}Z`);
  const diffMinutes = Math.max(0, Math.floor((Date.now() - target.getTime()) / 60000));
  if (diffMinutes <= STALE_SYNC_MINUTES_WARNING) return 'green';
  if (diffMinutes <= STALE_SYNC_MINUTES_DANGER) return 'yellow';
  return 'red';
};

const SuperAdminIntegrationSyncPanel = ({ org = 'NMA', daysWindow = 5, refreshTrigger = 0 }) => {
  const { accessToken } = useContext(AppContext);
  const [syncData, setSyncData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [statusFilter, setStatusFilter] = useState('ALL');
  const [uploadStatusFilter, setUploadStatusFilter] = useState('ALL');
  const [eventsMode, setEventsMode] = useState('errors'); // 'errors' | 'all'
  const [logsMode, setLogsMode] = useState('warn_error'); // 'warn_error' | 'all'
  const [expandedEvents, setExpandedEvents] = useState({});

  const fetchSyncPulse = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const response = await callApi(
        `/admin/super-dashboard/integration-sync?org=${org}&days=${daysWindow}`,
        accessToken,
        { method: 'GET' }
      );
      if (!response.ok) {
        throw new Error(`Failed to load integration sync stats for ${org}`);
      }
      const data = await response.json();
      setSyncData(data);
    } catch (err) {
      console.error('Error fetching integration sync stats:', err);
      setError(err.message || 'Failed to load integration sync data.');
    } finally {
      setLoading(false);
    }
  }, [accessToken, org, daysWindow]);

  useEffect(() => {
    fetchSyncPulse();
  }, [fetchSyncPulse, refreshTrigger]);

  const toggleEventMetadata = (eventId) => {
    setExpandedEvents((prev) => ({ ...prev, [eventId]: !prev[eventId] }));
  };

  if (loading && !syncData) {
    return (
      <Flex justify="center" align="center" py={10}>
        <Spinner size="lg" color="blue.500" mr={3} />
        <Text fontSize="sm" color="gray.600">Loading {org} Integration Server Sync data...</Text>
      </Flex>
    );
  }

  const syncHosts = syncData?.sync_hosts || [];
  const allTimeCounts = syncData?.all_time_org_counts || {};
  const orgSummary = syncData?.org_summary || {};
  const dailyCaseStatus = syncData?.daily_case_status || [];
  const recentCaseStatuses = syncData?.recent_case_statuses || [];
  const eventBreakdown = syncData?.event_breakdown || [];
  const recentEvents = eventsMode === 'errors'
    ? (syncData?.recent_error_events || [])
    : (syncData?.recent_all_events || []);
  const logByLevel = syncData?.log_by_level || [];
  const logByModule = syncData?.log_by_module || [];
  const recentLogs = logsMode === 'warn_error'
    ? (syncData?.recent_warn_error_logs || [])
    : (syncData?.recent_all_logs || []);

  const filteredCases = recentCaseStatuses.filter((c) => {
    const st = c.status || 'UNKNOWN';
    const up = c.upload_status || 'NONE';
    if (statusFilter !== 'ALL' && st !== statusFilter) return false;
    if (uploadStatusFilter !== 'ALL' && up !== uploadStatusFilter) return false;
    return true;
  });

  const distinctStatuses = Array.from(new Set(recentCaseStatuses.map((c) => c.status || 'UNKNOWN')));
  const distinctUploadStatuses = Array.from(new Set(recentCaseStatuses.map((c) => c.upload_status || 'NONE')));

  return (
    <Box mt={6}>
      <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={2}>
        <Box>
          <HStack spacing={2}>
            <Heading size="md" color="gray.800">
              Integration Server Data Sync ({org})
            </Heading>
            <Badge colorScheme="teal" fontSize="xs">SQLite → Core Postgres</Badge>
          </HStack>
          <Text fontSize="xs" color="gray.500">
            Raw visibility across integration_case_status, integration_events, and integration_logs
          </Text>
        </Box>

        <HStack spacing={2} flexWrap="wrap">
          {syncHosts.length === 0 ? (
            <Badge colorScheme="gray" px={2} py={1}>No hosts synced yet</Badge>
          ) : (
            syncHosts.map((h) => (
              <Badge
                key={h.source_host}
                colorScheme={getSyncFreshnessScheme(h.last_synced_at)}
                px={2.5}
                py={1}
                borderRadius="md"
                fontSize="xs"
              >
                {h.source_host}: {formatRelativeTime(h.last_synced_at)} ({h.case_status_total} cases / {h.events_total} ev / {h.logs_total} logs)
              </Badge>
            ))
          )}
          <Button size="xs" leftIcon={<RepeatIcon />} onClick={fetchSyncPulse} isLoading={loading}>
            Sync Refresh
          </Button>
        </HStack>
      </Flex>

      {error && (
        <Alert status="error" borderRadius="md" mb={4}>
          <AlertIcon />
          {error}
        </Alert>
      )}

      {/* Summary Strip for Synced Org */}
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={4} mb={6}>
        <Card shadow="xs" border="1px solid" borderColor="gray.200" bg="white">
          <CardBody py={3}>
            <Stat>
              <StatLabel fontSize="xs" color="gray.500">Synced Cases ({daysWindow}d / All-Time)</StatLabel>
              <StatNumber fontSize="xl">
                {orgSummary.case_statuses_in_window || 0}{' '}
                <Text as="span" fontSize="sm" color="gray.400" fontWeight="normal">
                  / {allTimeCounts.case_status_all_time || 0} total
                </Text>
              </StatNumber>
              <StatHelpText mb={0} fontSize="xs">
                Linked to Core: <b>{orgSummary.linked_core_cases || 0}</b> • Unlinked: <b>{orgSummary.unlinked_core_cases || 0}</b>
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card shadow="xs" border="1px solid" borderColor="gray.200" bg="white">
          <CardBody py={3}>
            <Text fontSize="xs" color="gray.500" fontWeight="medium" mb={1.5}>
              Worker Pipeline Statuses ({daysWindow}d)
            </Text>
            <HStack spacing={1.5} flexWrap="wrap">
              {(orgSummary.status_totals || []).length === 0 ? (
                <Text fontSize="xs" color="gray.400">No case statuses in {daysWindow}d window</Text>
              ) : (
                orgSummary.status_totals.map((s) => (
                  <Badge key={s.status} colorScheme={getStatusColor(s.status)} fontSize="xs">
                    {s.status}: {s.count}
                  </Badge>
                ))
              )}
            </HStack>
          </CardBody>
        </Card>

        <Card shadow="xs" border="1px solid" borderColor="gray.200" bg="white">
          <CardBody py={3}>
            <Text fontSize="xs" color="gray.500" fontWeight="medium" mb={1.5}>
              Outbound Upload Statuses ({daysWindow}d)
            </Text>
            <HStack spacing={1.5} flexWrap="wrap">
              {(orgSummary.upload_status_totals || []).length === 0 ? (
                <Text fontSize="xs" color="gray.400">No uploads in {daysWindow}d window</Text>
              ) : (
                orgSummary.upload_status_totals.map((u) => (
                  <Badge key={u.upload_status} colorScheme={getStatusColor(u.upload_status)} fontSize="xs">
                    {u.upload_status}: {u.count}
                  </Badge>
                ))
              )}
            </HStack>
          </CardBody>
        </Card>

        <Card shadow="xs" border="1px solid" borderColor="gray.200" bg="white">
          <CardBody py={3}>
            <Stat>
              <StatLabel fontSize="xs" color="gray.500">Events & Logs ({daysWindow}d / All-Time)</StatLabel>
              <StatNumber fontSize="md">
                Events: {orgSummary.events_in_window || 0} ({allTimeCounts.events_all_time || 0})
              </StatNumber>
              <StatHelpText mb={0} fontSize="xs">
                Logs: <b>{orgSummary.logs_in_window || 0}</b> ({allTimeCounts.logs_all_time || 0} total)
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Section 1: integration_case_status Daily Breakdown & Recent Cases */}
      <Grid templateColumns={{ base: '1fr', lg: '5fr 7fr' }} gap={6} mb={6}>
        <GridItem>
          <Card shadow="xs" border="1px solid" borderColor="gray.200" bg="white" h="100%">
            <CardHeader pb={2}>
              <Heading size="xs" color="gray.700" textTransform="uppercase">
                1A. Daily integration_case_status ({daysWindow}d)
              </Heading>
            </CardHeader>
            <CardBody pt={1}>
              <Box overflowX="auto">
                <Table size="sm" variant="simple">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th fontSize="2xs">Date</Th>
                      <Th fontSize="2xs" isNumeric>Total</Th>
                      <Th fontSize="2xs">Statuses</Th>
                      <Th fontSize="2xs">Upload Statuses</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {dailyCaseStatus.map((d) => (
                      <Tr key={d.date}>
                        <Td fontSize="xs" fontWeight="medium" whiteSpace="nowrap">{d.date}</Td>
                        <Td fontSize="xs" isNumeric fontWeight="bold">{d.total_cases}</Td>
                        <Td>
                          <HStack spacing={1} flexWrap="wrap">
                            {Object.entries(d.statuses || {}).map(([k, v]) => (
                              <Badge key={k} colorScheme={getStatusColor(k)} fontSize="2xs">
                                {k}: {v}
                              </Badge>
                            ))}
                          </HStack>
                        </Td>
                        <Td>
                          <HStack spacing={1} flexWrap="wrap">
                            {Object.entries(d.upload_statuses || {}).map(([k, v]) => (
                              <Badge key={k} colorScheme={getStatusColor(k)} fontSize="2xs">
                                {k}: {v}
                              </Badge>
                            ))}
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </CardBody>
          </Card>
        </GridItem>

        <GridItem>
          <Card shadow="xs" border="1px solid" borderColor="gray.200" bg="white" h="100%">
            <CardHeader pb={2}>
              <Flex justify="space-between" align="center" flexWrap="wrap" gap={2}>
                <Heading size="xs" color="gray.700" textTransform="uppercase">
                  1B. Recent Synced Cases ({filteredCases.length})
                </Heading>
                <HStack spacing={2}>
                  <Select size="xs" w="140px" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="ALL">All Statuses</option>
                    {distinctStatuses.map((st) => (
                      <option key={st} value={st}>{st}</option>
                    ))}
                  </Select>
                  <Select size="xs" w="140px" value={uploadStatusFilter} onChange={(e) => setUploadStatusFilter(e.target.value)}>
                    <option value="ALL">All Uploads</option>
                    {distinctUploadStatuses.map((up) => (
                      <option key={up} value={up}>{up}</option>
                    ))}
                  </Select>
                </HStack>
              </Flex>
            </CardHeader>
            <CardBody pt={1}>
              <Box overflowX="auto" maxH="300px" overflowY="auto">
                <Table size="sm" variant="simple">
                  <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
                    <Tr>
                      <Th fontSize="2xs">Case Key / IDs</Th>
                      <Th fontSize="2xs">Core Link</Th>
                      <Th fontSize="2xs">Status</Th>
                      <Th fontSize="2xs">Upload</Th>
                      <Th fontSize="2xs">Updated</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredCases.length === 0 ? (
                      <Tr>
                        <Td colSpan={5} textAlign="center" color="gray.400" fontSize="xs" py={6}>
                          No synced case records match filter.
                        </Td>
                      </Tr>
                    ) : (
                      filteredCases.map((c) => (
                        <Tr key={c.id} _hover={{ bg: 'gray.50' }}>
                          <Td fontSize="xs">
                            <Text fontWeight="semibold">{c.case_key}</Text>
                            <Text fontSize="2xs" color="gray.500">
                              rec: {c.customer_record_id || '—'} | ext: {c.external_case_id || '—'} ({c.source_host})
                            </Text>
                          </Td>
                          <Td fontSize="xs">
                            {c.internal_case_id ? (
                              <Badge colorScheme="green" fontSize="2xs">#{c.internal_case_id}</Badge>
                            ) : (
                              <Badge colorScheme="gray" fontSize="2xs">Unlinked</Badge>
                            )}
                          </Td>
                          <Td>
                            <Badge colorScheme={getStatusColor(c.status)} fontSize="2xs">
                              {c.status || 'UNKNOWN'}
                            </Badge>
                          </Td>
                          <Td>
                            <Badge colorScheme={getStatusColor(c.upload_status)} fontSize="2xs">
                              {c.upload_status || 'NONE'}
                            </Badge>
                          </Td>
                          <Td fontSize="2xs" color="gray.600" whiteSpace="nowrap">
                            {formatTimestamp(c.last_updated)}
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>
            </CardBody>
          </Card>
        </GridItem>
      </Grid>

      {/* Section 2: integration_events Breakdown & Feed */}
      <Grid templateColumns={{ base: '1fr', lg: '5fr 7fr' }} gap={6} mb={6}>
        <GridItem>
          <Card shadow="xs" border="1px solid" borderColor="gray.200" bg="white" h="100%">
            <CardHeader pb={2}>
              <Heading size="xs" color="gray.700" textTransform="uppercase">
                2A. integration_events Breakdown ({daysWindow}d)
              </Heading>
            </CardHeader>
            <CardBody pt={1}>
              <Box overflowX="auto" maxH="300px" overflowY="auto">
                <Table size="sm" variant="simple">
                  <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
                    <Tr>
                      <Th fontSize="2xs">Integration</Th>
                      <Th fontSize="2xs">Event Type</Th>
                      <Th fontSize="2xs">Status</Th>
                      <Th fontSize="2xs" isNumeric>Count</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {eventBreakdown.length === 0 ? (
                      <Tr>
                        <Td colSpan={4} textAlign="center" color="gray.400" fontSize="xs" py={6}>
                          No events in {daysWindow}d window.
                        </Td>
                      </Tr>
                    ) : (
                      eventBreakdown.map((eb, idx) => (
                        <Tr key={`${eb.integration_name}-${eb.event_type}-${eb.status}-${idx}`}>
                          <Td fontSize="xs">{eb.integration_name}</Td>
                          <Td fontSize="xs" fontWeight="medium">{eb.event_type}</Td>
                          <Td>
                            <Badge colorScheme={getStatusColor(eb.status)} fontSize="2xs">
                              {eb.status}
                            </Badge>
                          </Td>
                          <Td fontSize="xs" isNumeric fontWeight="bold">{eb.count}</Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>
            </CardBody>
          </Card>
        </GridItem>

        <GridItem>
          <Card shadow="xs" border="1px solid" borderColor="gray.200" bg="white" h="100%">
            <CardHeader pb={2}>
              <Flex justify="space-between" align="center">
                <Heading size="xs" color="gray.700" textTransform="uppercase">
                  2B. Recent Integration Events ({recentEvents.length})
                </Heading>
                <ButtonGroup size="xs" isAttached variant="outline">
                  <Button
                    colorScheme={eventsMode === 'errors' ? 'red' : 'gray'}
                    variant={eventsMode === 'errors' ? 'solid' : 'outline'}
                    onClick={() => setEventsMode('errors')}
                  >
                    Non-Success / Errors
                  </Button>
                  <Button
                    colorScheme={eventsMode === 'all' ? 'blue' : 'gray'}
                    variant={eventsMode === 'all' ? 'solid' : 'outline'}
                    onClick={() => setEventsMode('all')}
                  >
                    All Recent
                  </Button>
                </ButtonGroup>
              </Flex>
            </CardHeader>
            <CardBody pt={1}>
              <Box overflowX="auto" maxH="300px" overflowY="auto">
                <Table size="sm" variant="simple">
                  <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
                    <Tr>
                      <Th fontSize="2xs">Time</Th>
                      <Th fontSize="2xs">Case</Th>
                      <Th fontSize="2xs">Workflow / Event</Th>
                      <Th fontSize="2xs">Status</Th>
                      <Th fontSize="2xs">Message / Metadata</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {recentEvents.length === 0 ? (
                      <Tr>
                        <Td colSpan={5} textAlign="center" color="gray.400" fontSize="xs" py={6}>
                          No events to display.
                        </Td>
                      </Tr>
                    ) : (
                      recentEvents.map((ev) => {
                        const isExp = Boolean(expandedEvents[ev.id]);
                        const hasMeta = ev.metadata && Object.keys(ev.metadata).length > 0;
                        return (
                          <React.Fragment key={ev.id}>
                            <Tr _hover={{ bg: 'gray.50' }}>
                              <Td fontSize="2xs" whiteSpace="nowrap">{formatTimestamp(ev.timestamp)}</Td>
                              <Td fontSize="xs">
                                {ev.external_case_id || ev.customer_record_id || '—'}
                              </Td>
                              <Td fontSize="xs">
                                <Text fontWeight="semibold">{ev.event_type}</Text>
                                <Text fontSize="2xs" color="gray.500">{ev.integration_name}</Text>
                              </Td>
                              <Td>
                                <Badge colorScheme={getStatusColor(ev.status)} fontSize="2xs">
                                  {ev.status}
                                </Badge>
                              </Td>
                              <Td fontSize="xs">
                                <HStack justify="space-between">
                                  <Text noOfLines={2}>{ev.message || '—'}</Text>
                                  {hasMeta && (
                                    <Button
                                      size="2xs"
                                      variant="ghost"
                                      rightIcon={isExp ? <ChevronUpIcon /> : <ChevronDownIcon />}
                                      onClick={() => toggleEventMetadata(ev.id)}
                                    >
                                      JSON
                                    </Button>
                                  )}
                                </HStack>
                              </Td>
                            </Tr>
                            {hasMeta && (
                              <Tr>
                                <Td colSpan={5} p={0} borderBottom={isExp ? '1px solid' : 'none'} borderColor="gray.200">
                                  <Collapse in={isExp} animateOpacity>
                                    <Box bg="gray.900" color="green.200" p={2} fontSize="2xs" overflowX="auto">
                                      <Code bg="transparent" color="inherit" whiteSpace="pre">
                                        {JSON.stringify(ev.metadata, null, 2)}
                                      </Code>
                                    </Box>
                                  </Collapse>
                                </Td>
                              </Tr>
                            )}
                          </React.Fragment>
                        );
                      })
                    )}
                  </Tbody>
                </Table>
              </Box>
            </CardBody>
          </Card>
        </GridItem>
      </Grid>

      {/* Section 3: integration_logs Breakdown & Stream */}
      <Grid templateColumns={{ base: '1fr', lg: '4fr 8fr' }} gap={6}>
        <GridItem>
          <Card shadow="xs" border="1px solid" borderColor="gray.200" bg="white" h="100%">
            <CardHeader pb={2}>
              <Heading size="xs" color="gray.700" textTransform="uppercase">
                3A. integration_logs by Level & Module ({daysWindow}d)
              </Heading>
            </CardHeader>
            <CardBody pt={1}>
              <HStack spacing={2} mb={3} flexWrap="wrap">
                {logByLevel.map((lvl) => (
                  <Badge key={lvl.level} colorScheme={getStatusColor(lvl.level)} fontSize="xs" px={2} py={0.5}>
                    {lvl.level}: {lvl.count}
                  </Badge>
                ))}
              </HStack>
              <Box overflowX="auto" maxH="240px" overflowY="auto">
                <Table size="sm" variant="simple">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th fontSize="2xs">Module / Integration</Th>
                      <Th fontSize="2xs">Level</Th>
                      <Th fontSize="2xs" isNumeric>Count</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {logByModule.length === 0 ? (
                      <Tr>
                        <Td colSpan={3} textAlign="center" color="gray.400" fontSize="xs" py={4}>
                          No logs in {daysWindow}d window.
                        </Td>
                      </Tr>
                    ) : (
                      logByModule.map((m, idx) => (
                        <Tr key={`${m.integration}-${m.module}-${m.level}-${idx}`}>
                          <Td fontSize="xs">
                            <Text fontWeight="medium">{m.module}</Text>
                            <Text fontSize="2xs" color="gray.500">{m.integration}</Text>
                          </Td>
                          <Td>
                            <Badge colorScheme={getStatusColor(m.level)} fontSize="2xs">{m.level}</Badge>
                          </Td>
                          <Td fontSize="xs" isNumeric fontWeight="bold">{m.count}</Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>
            </CardBody>
          </Card>
        </GridItem>

        <GridItem>
          <Card shadow="xs" border="1px solid" borderColor="gray.200" bg="white" h="100%">
            <CardHeader pb={2}>
              <Flex justify="space-between" align="center">
                <Heading size="xs" color="gray.700" textTransform="uppercase">
                  3B. Recent Integration Logs ({recentLogs.length})
                </Heading>
                <ButtonGroup size="xs" isAttached variant="outline">
                  <Button
                    colorScheme={logsMode === 'warn_error' ? 'orange' : 'gray'}
                    variant={logsMode === 'warn_error' ? 'solid' : 'outline'}
                    onClick={() => setLogsMode('warn_error')}
                  >
                    WARN / ERROR Only
                  </Button>
                  <Button
                    colorScheme={logsMode === 'all' ? 'blue' : 'gray'}
                    variant={logsMode === 'all' ? 'solid' : 'outline'}
                    onClick={() => setLogsMode('all')}
                  >
                    All Logs
                  </Button>
                </ButtonGroup>
              </Flex>
            </CardHeader>
            <CardBody pt={1}>
              <Box overflowX="auto" maxH="280px" overflowY="auto">
                <Table size="sm" variant="simple">
                  <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
                    <Tr>
                      <Th fontSize="2xs">Time</Th>
                      <Th fontSize="2xs">Level</Th>
                      <Th fontSize="2xs">Case</Th>
                      <Th fontSize="2xs">Module</Th>
                      <Th fontSize="2xs">Message</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {recentLogs.length === 0 ? (
                      <Tr>
                        <Td colSpan={5} textAlign="center" color="gray.400" fontSize="xs" py={6}>
                          No log entries to display.
                        </Td>
                      </Tr>
                    ) : (
                      recentLogs.map((lg) => (
                        <Tr key={lg.id} _hover={{ bg: 'gray.50' }}>
                          <Td fontSize="2xs" whiteSpace="nowrap">{formatTimestamp(lg.timestamp)}</Td>
                          <Td>
                            <Badge colorScheme={getStatusColor(lg.level)} fontSize="2xs">{lg.level}</Badge>
                          </Td>
                          <Td fontSize="xs">{lg.external_case_id || lg.customer_record_id || '—'}</Td>
                          <Td fontSize="xs">
                            <Text fontWeight="medium">{lg.module || '—'}</Text>
                            <Text fontSize="2xs" color="gray.500">{lg.integration || ''}</Text>
                          </Td>
                          <Td fontSize="xs">
                            <Text whiteSpace="pre-wrap" wordBreak="break-word">{lg.message}</Text>
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>
            </CardBody>
          </Card>
        </GridItem>
      </Grid>
    </Box>
  );
};

export default SuperAdminIntegrationSyncPanel;
